import React, { createContext, useContext, useState, useCallback } from 'react';
import { apiServices } from '../utils/apiServices';
import type { InjectedAccountWithMeta } from "@polkadot/extension-inject/types";
import { ApiPromise } from "@polkadot/api";
import type { IPoolBalanceHistory, TPoolHistoryType } from '../utils/types';
// Add type declaration for window.injectedWeb3
declare global {
  interface Window {
    injectedWeb3?: Record<string, any>;
  }
}

interface DepositorBalance {
  address: string;
  amount: number;
}

interface PoolBalances {
  locked: number;
  total: number;
  staked: number;
}

interface IAppContextType {
  depositorBalances: DepositorBalance[];
  poolBalanceHistory: IPoolBalanceHistory[];
  poolBalances: PoolBalances;
  accounts: InjectedAccountWithMeta[];
  activeAccount: InjectedAccountWithMeta | null;
  isLoading: boolean;
  error: string | null;
  api: ApiPromise | null;
  setAccounts: (accounts: InjectedAccountWithMeta[]) => void;
  addDepositorBalance: (address: string, amount: number) => void;
  updateDepositorBalance: (address: string, amount: number) => void;
  removeDepositorBalance: (address: string) => void;
  getPoolBalance: (type: TPoolHistoryType) => void;
  disconnectWallet: () => void;
  setActiveAccount: (account: InjectedAccountWithMeta | null) => void;
  setIsLoading: (loading: boolean) => void;
}

const AppContext = createContext<IAppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [depositorBalances, setDepositorBalances] = useState<DepositorBalance[]>([]);
  const [poolBalanceHistory, setPoolBalanceHistory] = useState<IPoolBalanceHistory[]>([]);
  const [accounts, setAccountsState] = useState<InjectedAccountWithMeta[]>(() => {
    const savedAccounts = localStorage.getItem('accounts');
    return savedAccounts ? JSON.parse(savedAccounts) : [];
  });
  const [activeAccount, setActiveAccount] = useState<InjectedAccountWithMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [api, setApi] = useState<ApiPromise | null>(null);
  const [poolBalances, setPoolBalances] = useState<PoolBalances>({
    locked: 0,
    total: 0,
    staked: 0
  });

  const getPoolBalance = useCallback(async (type: TPoolHistoryType = "day") => {
    try {
      const res = await apiServices.pool.getBalances(type);
      setPoolBalances({
        locked: parseFloat(res.pool_locked_balance as string),
        total: parseFloat(res.pool_total_balance as string),
        staked: parseFloat(res.pool_staked_balance as string)
      });
      setPoolBalanceHistory(res.pool_history as IPoolBalanceHistory[]);
    } catch (error) {
      console.error('Failed to fetch pool balances:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch pool balances');
      // Set default values in case of error
      setPoolBalances({
        locked: 0,
        total: 0,
        staked: 0
      });
    }
  }, [])

  const disconnectWallet = useCallback(() => {
    setAccounts([]);
    setActiveAccount(null);
    setApi(null); // Optionally close the API connection
    localStorage.removeItem('accounts');
    // Optionally remove signatures for all accounts
    accounts.forEach(acc => localStorage.removeItem(acc.address));
  }, [accounts]);

  const setAccounts = useCallback((accounts: InjectedAccountWithMeta[]) => {
    setAccountsState(accounts);
    if (accounts.length > 0) {
      localStorage.setItem('accounts', JSON.stringify(accounts));
    } else {
      localStorage.removeItem('accounts');
    }
  }, []);

  const addDepositorBalance = useCallback((address: string, amount: number) => {
    setDepositorBalances(prev => [...prev, { address, amount }]);
  }, []);

  const updateDepositorBalance = useCallback((address: string, amount: number) => {
    setDepositorBalances(prev =>
      prev.map(depositor =>
        depositor.address === address ? { ...depositor, amount } : depositor
      )
    );
  }, []);

  const removeDepositorBalance = useCallback((address: string) => {
    setDepositorBalances(prev =>
      prev.filter(depositor => depositor.address !== address)
    );
  }, []);

  const value = {
    depositorBalances,
    poolBalanceHistory,
    poolBalances,
    accounts,
    isLoading,
    error,
    api,
    activeAccount,
    setActiveAccount,
    setAccounts,
    addDepositorBalance,
    updateDepositorBalance,
    removeDepositorBalance,
    getPoolBalance,
    disconnectWallet,
    setIsLoading
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};